import { ChangeEvent, useCallback } from 'react';
import type { Dispatch } from 'redux';
import { connect, useDispatch } from 'react-redux';

import { IUploadFile } from 'CorePackage/MainModule/interfaces/IUploadFile';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { FeedbackFormType } from '../types/FeedbackFormType';
import { FeedbackFormBase } from './FeedbackFormBase';
import { feedbackPost, feedbackSetError, feedbackSetFiles, feedbackUploadFile } from '../actions/feedbackActions';
import {
  getFeedbackFiles,
  getFeedbackError,
  getFeedbackKnmData,
  getFeedbackPlanData,
} from '../selectors/feedbackSelectors';
import { IKnmPlan } from '../../../PlanListPackage/MainModule/interfaces/IKnmPlan';
import { getUserInfo, getUserRole } from '../../../AuthPackage/MainModule/selectors/authSelectors';
import { IUserInfo } from '../../../APIPackage/AuthModule/interfaces/IUserInfo';
import { IUserRole } from '../../../APIPackage/AuthModule/interfaces/IUserRole';
import { IFeedbackKnmData } from '../interfaces/IFeedbackKnmData';
import { approveRequiredSelectOptions } from '../../../KnmErknmPackage/MainModule/constants/approveRequiredSelectOptions';
import { jobMap } from '../../../PersonalAccountPackage/MainModule/common/jobMap';

interface IFeedbackFormStoreProps {
  files: IUploadFile[];
  error: string;
  knmData: IFeedbackKnmData | undefined;
  planData: IKnmPlan | undefined;
  user?: IUserInfo;
  role?: IUserRole;
}

const FeedbackFormWithoutStore = ({ files, error, knmData, planData, user, role }: IFeedbackFormStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const getLink = () => {
    const linkPart = knmData ? 'knm' : 'plan';
    const id = knmData ? knmData.id : planData?.id;

    return `${window.location.origin}/private/${linkPart}/${id}`;
  };

  const userInfo = () => {
    const fio = {
      first: user?.userProfile.nameFirst,
      middle: user?.userProfile.nameMiddle,
      last: user?.userProfile.nameLast,
      login: user?.userAccount?.login ? `(${user?.userAccount?.login?.toUpperCase()})` : undefined,
    };

    let info = Object.values(fio)
      .filter(v => v !== undefined)
      .join(' ');

    if (role) {
      info += `; ${jobMap[role.sysname]}`;
    }

    return info;
  };

  const setTextDescription = (text: string) => {
    if (knmData) {
      return `Номер паспорта КНМ: ${knmData.erpId}\nСтатус паспорта КНМ: ${knmData.status}\nСтатус подписания КНМ: ${
        knmData.signed ? 'Подписано' : 'Не подписано'
      }\nХарактер КНМ: ${knmData.type}\nВид КНМ: ${knmData.kindKnm}\nНеобходимость согласования: ${
        approveRequiredSelectOptions.find(x => x.value === knmData.approveRequired)?.label || 'Не требуется'
      }\nСсылка на паспорт КНМ: ${getLink()}\n Пользователь: ${userInfo()}\n***\n${text}`;
    }
    if (planData) {
      return `Номер плана КНМ: ${
        planData.id
      }\nСтатус плана КНМ:: ${planData?.erknmStatus?.toString()}\nСтатус подписания: ${
        planData.signed ? 'Подписано' : 'Не подписано'
      }\nСсылка на план КНМ: ${getLink()}\n Пользователь: ${userInfo()}\n***\n${text}`;
    }

    return text;
  };

  const uploadFile = useCallback(
    (e: ChangeEvent<HTMLInputElement>, nameIfExists?: string) => {
      dispatch(feedbackUploadFile(e, nameIfExists));
    },
    [dispatch],
  );

  const removeFile = useCallback(
    (fileName: string) => {
      const fileIndex = files.findIndex(file => file.fileName === fileName);

      dispatch(feedbackSetFiles([...files.slice(0, fileIndex), ...files.slice(fileIndex + 1)]));
    },
    [dispatch, files],
  );

  const addFeedback = useCallback(
    (values: FeedbackFormType) => {
      const textDescription = setTextDescription(values.text);

      dispatch(feedbackPost(values, files, textDescription));
    },
    [dispatch, files],
  );

  const setError = useCallback(
    (err: string) => {
      dispatch(feedbackSetError(err));
    },
    [dispatch],
  );

  return (
    <FeedbackFormBase
      error={error}
      files={files}
      handleUploadInputChange={uploadFile}
      onFormSubmit={addFeedback}
      setError={setError}
      removeFile={removeFile}
      knmData={knmData}
      planData={planData}
      userInfo={userInfo}
      getLink={getLink}
    />
  );
};

const mapStateToProps = (state: IRootState): IFeedbackFormStoreProps => ({
  files: getFeedbackFiles(state),
  error: getFeedbackError(state),
  knmData: getFeedbackKnmData(state),
  planData: getFeedbackPlanData(state),
  user: getUserInfo(state),
  role: getUserRole(state),
});

export const FeedbackForm = connect(mapStateToProps)(FeedbackFormWithoutStore);
